import { enviroment } from "../../enviroment";
import {
  TransferLogoType,
  TransferMethod,
} from "../../core/models/entities/LogoOptions";

/**
 * Flag REACT_APP_LOGO_LIST_I18N_ENABLED (Nichlas 17/10): translated DISPLAY
 * texts for the logo/design lists' Status, Type and Method filters, the
 * method line under a logo's title and the Type/Method lines in the logo
 * and design popups - plus ONE merged "Screen Print" value for every
 * screen-print method variant.
 *
 * Everything here is pure (no React) and display-only:
 * - filter VALUES (enum names, NAV type keys, method keys) are never changed
 *   - only what the dropdown shows for them;
 * - unknown data falls back to the raw text, so a NAV method this map does
 *   not know is shown exactly as today;
 * - with the flag off every function returns today's text (byte-identical).
 *
 * Logic that COMPARES these texts (getTypeText in the PDFs, the heating
 * switch, LCA, LogoPicker's "Draft" filter value) must never call into this
 * file - see logoListI18n.logicPins.test.ts.
 */

export type Translate = (key: string) => string;

/**
 * Value of the merged "Screen Print" option in the Method filter. It is a
 * UI-only marker: the view models rewrite it to typeFilter=DIREK TRYK before
 * the query is built (rewriteScreenPrintMethodFilter), so it never reaches
 * the API. The design list wraps its method values in ':' (':KEY:').
 */
export const SCREEN_PRINT_METHOD_FILTER_VALUE = "__screenPrint__";
export const DESIGN_SCREEN_PRINT_METHOD_FILTER_VALUE = `:${SCREEN_PRINT_METHOD_FILTER_VALUE}:`;
/** NAV/Lionel type key of every screen-print method (API LogoType "DIREK TRYK"). */
export const SCREEN_PRINT_TYPE_KEY = "DIREK TRYK";
export const TYPE_FILTER_KEY = "typeFilter";
export const LOGO_METHOD_FILTER_KEY = "transferMethod.key";
export const DESIGN_METHOD_FILTER_KEY = "methodFilter";

const SCREEN_PRINT_LABEL_KEY = "logoListI18n.type.screenPrint";
const EMBROIDERY_LABEL_KEY = "logoList.embroidery";

export const isLogoListI18nOn = (): boolean => enviroment.logoListI18nEnabled;

const norm = (s: string | null | undefined): string =>
  (s ?? "").trim().toLowerCase();

const upperFirst = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

const lowerFirst = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1);

/** NAV type "DIREK TRYK" or a type/nickname containing "screen print". */
export function isScreenPrintTypeName(type: string | null | undefined): boolean {
  const n = norm(type);
  return n === "direk tryk" || n.includes("screen print");
}

export function isEmbroideryTypeName(type: string | null | undefined): boolean {
  const n = norm(type);
  return n === "broderi" || n === "embroidery";
}

/**
 * A method is a screen-print variant when its NAV type is DIREK TRYK, or its
 * type/display name contains "screen print" ("Screen Print", "Screen Print
 * W/ Base", ...), or it is the merged option itself. Same rule as
 * TransferMethodUtil.isScreenPrintMethod plus the DIREK TRYK type.
 */
export function isScreenPrintMethodEntry(
  method: Partial<TransferMethod> | null | undefined,
): boolean {
  if (!method) return false;
  return (
    method.key === SCREEN_PRINT_METHOD_FILTER_VALUE ||
    isScreenPrintTypeName(method.type) ||
    norm(method.displayName).includes("screen print")
  );
}

// ---------------------------------------------------------------- status

const STATUS_KEYS: Readonly<Record<string, string>> = {
  approved: "logoListI18n.status.approved",
  rejected: "logoListI18n.status.rejected",
  pending: "logoListI18n.status.pending",
  draft: "logoListI18n.status.draft",
};

/** Status filter option text. The option VALUE stays the enum name. */
export function getStatusLabel(state: string, t: Translate): string {
  if (!isLogoListI18nOn()) return state;
  const key = STATUS_KEYS[norm(state)];
  return key ? t(key) : state;
}

// ------------------------------------------------------------------ type

const TYPE_KEYS_BY_TYPE_KEY: Readonly<Record<string, string>> = {
  transfer: "logoListI18n.type.transfer",
  "direk tryk": SCREEN_PRINT_LABEL_KEY,
  "screen print": SCREEN_PRINT_LABEL_KEY,
  broderi: "logoListI18n.type.embroidery",
  embroidery: "logoListI18n.type.embroidery",
  badge: "logoListI18n.type.badge",
};

function typeLabelKey(rawType: string | null | undefined): string | undefined {
  const n = norm(rawType);
  if (!n) return undefined;
  return TYPE_KEYS_BY_TYPE_KEY[n] ?? (n.includes("screen print") ? SCREEN_PRINT_LABEL_KEY : undefined);
}

/** Type filter option text (typeKey first, nickname second, raw nickname last). */
export function getTypeLabel(
  type: Pick<TransferLogoType, "typeKey" | "nickname">,
  t: Translate,
): string {
  if (!isLogoListI18nOn()) return type.nickname;
  const key = typeLabelKey(type.typeKey) ?? typeLabelKey(type.nickname);
  return key ? t(key) : type.nickname;
}

/**
 * Type text for a row/popup that only knows transferMethod.type (the raw
 * NAV type). Unknown types fall back to today's lower-cased raw type.
 */
export function getTypeLabelFromRaw(
  rawType: string | null | undefined,
  t: Translate,
): string {
  const key = typeLabelKey(rawType);
  return key ? t(key) : norm(rawType);
}

// ---------------------------------------------------------------- method

/**
 * Display name -> translation key. Keys are Lionel's English nicknames
 * (and the Danish ones that live in the English data), normalised.
 * Anything not listed is shown raw.
 */
const METHOD_KEYS: Readonly<Record<string, string>> = {
  silicone: "logoListI18n.method.silicone",
  "embroidery badge": "logoListI18n.method.embroideryBadge",
  embroidery: EMBROIDERY_LABEL_KEY,
  broderi: EMBROIDERY_LABEL_KEY,
  standard: "logoListI18n.method.standard",
  digital: "compLogoDesign.digital",
  "digital dtf": "logoListI18n.method.digitalDtf",
  name: "name",
  navn: "name",
  stofmærke: "logoListI18n.method.fabricLabel",
  "fabric label": "logoListI18n.method.fabricLabel",
  "water reflex": "logoListI18n.method.waterReflex",
  "vand refleks": "logoListI18n.method.waterReflex",
  "plotter reflex": "logoListI18n.method.plotterReflex",
  "plotter refleks": "logoListI18n.method.plotterReflex",
};

/**
 * Method text + whether it came from the map (translated) or is the raw
 * NAV name shown as a fallback. Only the row label needs the distinction.
 */
function methodLabelEntry(
  method: Partial<TransferMethod> | null | undefined,
  t: Translate,
): { label: string; translated: boolean } {
  const raw = method?.displayName ?? "";
  if (!isLogoListI18nOn()) return { label: raw, translated: false };
  if (isScreenPrintMethodEntry(method))
    return { label: t(SCREEN_PRINT_LABEL_KEY), translated: true };
  const key = METHOD_KEYS[norm(raw)];
  return key ? { label: t(key), translated: true } : { label: raw, translated: false };
}

/**
 * Method text (filter option, popup Method line). Every screen-print variant
 * -> the ONE merged label; known names translated; unknown names raw.
 */
export function getMethodLabel(
  method: Partial<TransferMethod> | null | undefined,
  t: Translate,
): string {
  return methodLabelEntry(method, t).label;
}

/**
 * The Method filter's option list: every screen-print variant replaced by
 * ONE merged option (at the position of the first variant). Off = the list
 * untouched (same array instance).
 */
export function mergeScreenPrintMethods(
  methods: TransferMethod[],
  t: Translate,
): TransferMethod[] {
  if (!isLogoListI18nOn()) return methods;
  const out: TransferMethod[] = [];
  let merged = false;
  for (const m of methods) {
    if (!isScreenPrintMethodEntry(m)) {
      out.push(m);
      continue;
    }
    if (merged) continue;
    merged = true;
    out.push({
      ...m,
      key: SCREEN_PRINT_METHOD_FILTER_VALUE,
      typeAndKey: `${SCREEN_PRINT_TYPE_KEY}_${SCREEN_PRINT_METHOD_FILTER_VALUE}`,
      type: SCREEN_PRINT_TYPE_KEY,
      displayName: t(SCREEN_PRINT_LABEL_KEY),
    });
  }
  return out;
}

export interface ActiveFilterLike {
  key: string;
  value: string | number | boolean | undefined;
}

/**
 * Before the query is built: the merged Method option becomes
 * typeFilter=DIREK TRYK (the API filters BaseLogo.TypeFilter /
 * Design.TypeFilter with it already - every screen-print method in ONE
 * call, no new endpoint). Any other filter is kept as is - a Type filter
 * set to something else stays and the API ANDs the two (0 rows), exactly
 * what the two chips then say. Off, or no merged option present = the same
 * array instance back.
 */
export function rewriteScreenPrintMethodFilter<F extends ActiveFilterLike>(
  filters: F[],
  methodFilterKey: string,
  mergedValue: string,
): F[] {
  if (!isLogoListI18nOn()) return filters;
  const isMerged = (f: F) => f.key === methodFilterKey && f.value === mergedValue;
  if (!filters.some(isMerged)) return filters;
  const rest = filters.filter(
    (f) =>
      !isMerged(f) &&
      !(f.key === TYPE_FILTER_KEY && f.value === SCREEN_PRINT_TYPE_KEY),
  );
  return [...rest, { key: TYPE_FILTER_KEY, value: SCREEN_PRINT_TYPE_KEY } as F];
}

/**
 * Remembered filters (localStorage) with the flag OFF again: a merged
 * Method value stored while it was on would be sent raw to the API (0
 * rows, nothing to clear). Drops it in place, like the Color filter guard.
 */
export function dropMergedScreenPrintFilter<T extends Record<string, unknown>>(
  stored: T,
): T {
  for (const key of [LOGO_METHOD_FILTER_KEY, DESIGN_METHOD_FILTER_KEY]) {
    const value = stored[key];
    if (
      value === SCREEN_PRINT_METHOD_FILTER_VALUE ||
      value === DESIGN_SCREEN_PRINT_METHOD_FILTER_VALUE
    ) {
      delete stored[key];
    }
  }
  return stored;
}

// ------------------------------------------------------------- row label

/**
 * Flag-on text under a logo's title: "Direkte tryk" for every screen-print
 * method, "Broderi" for embroidery, otherwise "<Type>, <method>" with the
 * method's first letter lowered («Transfer, digital», Nichlas' wording).
 * Unknown type -> raw type with a capital; unknown method -> the raw NAV
 * name exactly as delivered («Transfer, Vand Sport», never «vand Sport»).
 * Only called by transferMethodLabel while the flag is on.
 */
export function transferMethodLabelI18n(
  method: Partial<TransferMethod> | undefined | null,
  t: Translate,
): string {
  if (isScreenPrintMethodEntry(method)) return t(SCREEN_PRINT_LABEL_KEY);
  if (isEmbroideryTypeName(method?.type)) return t(EMBROIDERY_LABEL_KEY);
  const typeKey = typeLabelKey(method?.type);
  const typeLabel = typeKey ? t(typeKey) : upperFirst(norm(method?.type));
  const entry = methodLabelEntry(method, t);
  const methodLabel = entry.translated ? lowerFirst(entry.label) : entry.label;
  if (!typeLabel) return methodLabel;
  if (!methodLabel) return typeLabel;
  return `${typeLabel}, ${methodLabel}`;
}
